import {
  type DuplicateQBankStructureMutation,
  useDuplicateQBankStructureMutation,
} from '@exam-management/graphql';
import { Dialog, DialogTitle, Divider } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useMemo } from 'react';

import {
  type DuplicateQBankStructureFormInput,
  DuplicateQBankStructureForm,
} from '../../components';

interface DuplicateQBankStructureModalProps {
  open: boolean;
  onClose: () => void;
  onCompleted?: (res: DuplicateQBankStructureMutation) => void;
  name: string;
  qBankStructureId: string;
}

export default function DuplicateQBankStructureModal(
  props: Readonly<DuplicateQBankStructureModalProps>,
) {
  const { open: isOpen, onClose, onCompleted, qBankStructureId, name } = props;

  const { showSnackbar } = useSnackbar();
  const [duplicateQBankStructure, { loading: isLoading }] = useDuplicateQBankStructureMutation({
    onCompleted: (res) => {
      showSnackbar({
        message: 'QBank Structure Duplicated ',
        variant: 'success',
      });
      onCompleted?.(res);
    },
    onError: () => {
      showSnackbar({
        message: 'Unable to duplicate QBank Structure',
        variant: 'error',
      });
    },
  });

  const handleSubmit = async (data: DuplicateQBankStructureFormInput) => {
    await duplicateQBankStructure({
      variables: {
        input: {
          id: qBankStructureId,
          name: data.name,
        },
      },
    });
  };

  const defaultValues = useMemo(() => {
    return { name: `Copy of ${name}` };
  }, [name]);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle onClose={onClose}>Duplicate QBank Structure</DialogTitle>
      <Divider />
      <DuplicateQBankStructureForm
        onClose={onClose}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        loading={isLoading}
      />
    </Dialog>
  );
}
